import { Form, InputNumber, Modal, Space } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { ReactElement, useCallback, useState } from 'react';
import { Dispatch } from 'redux';
import IApplication from '~Api/Application/IApplication';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import ConditionTypeEnum from '~Api/Application/ConditionTypeEnum';
import { applicationPropertyValuerValuationReceivedAction } from '~Applications/actions';
import UploadConditionDocument from '~Applications/Application/UploadConditionDocument';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import IValuer from '~Valuers/IValuer';
import { useDispatch } from 'react-redux';

export default function EnterValuationModal(props: {
    application: IApplication;
    applicationProperty: IApplicationProperty;
    isOpen: boolean;
    onCancel: () => void;
    valuer: IValuer;
}): ReactElement {
    const {
        application,
        applicationProperty,
        isOpen,
        onCancel,
        valuer,
    } = props;

    const dispatch: Dispatch = useDispatch();

    const [ valuationInspectionDate, setValuationInspectionDate ] = useState<string | null>(null);
    const [ valuationReceivedDateTime, setValuationReceivedDateTime ] = useState<string | null>(null);
    const [ valuationValue, setValuationValue ] = useState<number | null>(null);

    const valuationInspectionDateDayJs: Dayjs | null = valuationInspectionDate ? dayjs(valuationInspectionDate) : null;
    const valuationReceivedDateTimeDayjs: Dayjs | null = valuationReceivedDateTime ? dayjs(valuationReceivedDateTime) : null;

    const onChangeValuationInspectionDate: (date: Dayjs) => void = useCallback((date: Dayjs): void => {
        setValuationInspectionDate((previousValuationInspectionDate: string | null): string => dayjs(
            `${date.format('YYYY-MM-DD')} ${dayjs(previousValuationInspectionDate || undefined).format('HH:mm')}`,
        ).format());
    }, []);

    const onChangeValuationReceivedDate: (date: Dayjs) => void = useCallback((date: Dayjs): void => {
        setValuationReceivedDateTime((previousValuationReceivedDateTime: string | null): string => dayjs(
            `${date.format('YYYY-MM-DD')} ${dayjs(previousValuationReceivedDateTime || undefined).format('HH:mm')}`,
        ).format());
    }, []);

    const onChangeValuationReceivedTime: (time: Dayjs) => void = useCallback((time: Dayjs): void => {
        setValuationReceivedDateTime((previousValuationReceivedDateTime: string | null): string => dayjs(
            `${dayjs(previousValuationReceivedDateTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`,
        ).format());
    }, []);

    const onChangeValuationValue: (value: number) => void = useCallback((value: number): void => {
        setValuationValue(value);
    }, []);

    const onClickOk: () => void = useCallback((): void => {
        dispatch(applicationPropertyValuerValuationReceivedAction(
            applicationProperty.uuid,
            valuer.uuid,
            valuationInspectionDate,
            valuationReceivedDateTime,
            valuationValue,
        ));

        onCancel();
    }, [
        applicationProperty.uuid,
        dispatch,
        onCancel,
        valuationInspectionDate,
        valuationReceivedDateTime,
        valuationValue,
        valuer.uuid,
    ]);

    return (
        <Modal
            okText='Enter Valuation'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Enter Valuation'
            wrapClassName='property-valuation-enter-valuation-modal'
        >
            <Form.Item className='received-date' label='Received Date'>
                <Space>
                    <DatePicker
                        className='received-date-datepicker'
                        format='DD/MM/YYYY'
                        onChange={onChangeValuationReceivedDate}
                        value={valuationReceivedDateTimeDayjs}
                    />
                    <TimePicker
                        className='received-date-timepicker'
                        format='H:mm'
                        onChange={onChangeValuationReceivedTime}
                        value={valuationReceivedDateTimeDayjs}
                    />
                </Space>
            </Form.Item>
            <Form.Item className='amount' label='Amount'>
                <InputNumber
                    addonBefore='$'
                    min={0}
                    onChange={onChangeValuationValue}
                    value={valuationValue}
                />
            </Form.Item>
            <Form.Item className='inspection-date' label='Inspection Date'>
                <DatePicker
                    className='inspection-date-datepicker'
                    format='DD/MM/YYYY'
                    onChange={onChangeValuationInspectionDate}
                    value={valuationInspectionDateDayJs}
                />
            </Form.Item>
            <Form.Item label='Upload Report'>
                <UploadConditionDocument
                    application={application}
                    applicationProperty={applicationProperty}
                    conditionType={ConditionTypeEnum.Valuation}
                />
            </Form.Item>
        </Modal>
    );
}
